import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import LogoMark from "@/components/LogoMark";
import {
  LayoutDashboard, FilePlus2, ShieldCheck, Library, Globe2, Users, Radar,
  ScrollText, BarChart3, LogOut, Sun, Moon, Menu, X, Database, GitCompare, BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Painel", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/cadastro", label: "Cadastro de Semente", icon: FilePlus2, testid: "nav-cadastro" },
  { to: "/revisao", label: "Revisão Humana", icon: ShieldCheck, testid: "nav-revisao", roles: ["admin", "coordenador", "revisor"] },
  { to: "/revisao-legada", label: "Revisão Legada (Diff IA)", icon: GitCompare, testid: "nav-revisao-legada", roles: ["admin", "coordenador", "revisor"] },
  { to: "/biblioteca", label: "Biblioteca", icon: Library, testid: "nav-biblioteca" },
  { to: "/mapa", label: "Mapa Geográfico", icon: Globe2, testid: "nav-mapa" },
  { to: "/coleta", label: "Coleta Automática", icon: Radar, testid: "nav-coleta", roles: ["admin", "coordenador"] },
  { to: "/coleta-publica", label: "Coleta Pública", icon: Globe2, testid: "nav-coleta-publica", roles: ["admin", "coordenador"] },
  { to: "/legado", label: "Importar Base Legada", icon: Database, testid: "nav-legado", roles: ["admin", "coordenador"] },
  { to: "/produtividade", label: "Produtividade", icon: BarChart3, testid: "nav-produtividade", roles: ["admin", "coordenador"] },
  { to: "/usuarios", label: "Usuários", icon: Users, testid: "nav-usuarios", roles: ["admin", "coordenador"] },
  { to: "/auditoria", label: "Auditoria", icon: ScrollText, testid: "nav-auditoria", roles: ["admin", "coordenador"] },
  { to: "/legendas", label: "Legendas do Sistema", icon: BookOpen, testid: "nav-legendas" },
];

const COLLAPSE_KEY = "nostradamus-sidebar";

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  // Desktop sidebar collapsed state (persisted) + mobile drawer state
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch (e) { console.warn("Sidebar: localStorage indisponível", e); return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch (e) { console.warn("Sidebar: falha ao persistir estado", e); }
  }, [collapsed]);

  const toggleSidebar = () => {
    if (window.innerWidth < 768) setMobileOpen((v) => !v);
    else setCollapsed((v) => !v);
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const canSee = (item) => {
    if (!item.roles) return true;
    if (user?.perfil === "admin") return true;
    return item.roles.includes(user?.perfil);
  };

  const sidebarWidth = collapsed ? "w-16" : "w-64";

  return (
    <div className="min-h-screen flex grain overflow-x-hidden">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`bg-[var(--bg)] border-r border-[var(--border)] fixed top-0 left-0 h-screen flex flex-col z-40 transition-all duration-300
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} ${sidebarWidth}`}
        data-testid="sidebar"
        data-collapsed={collapsed}
      >
        <div className={`h-14 border-b border-[var(--border)] flex items-center flex-shrink-0 ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}>
          {collapsed ? (
            <img src="/assets/logo-nostradamus-oficial.png?v=3" alt="Projeto Nostradamus" className="w-9 h-9 object-contain" />
          ) : (
            <LogoMark small />
          )}
          {!collapsed && (
            <button
              onClick={() => setMobileOpen(false)}
              className="md:hidden p-1 text-[var(--text-muted)] hover:text-[var(--text)]"
              data-testid="sidebar-close-mobile"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <nav className="flex-1 py-4 scrollbar-thin overflow-y-auto overflow-x-hidden">
          {NAV.filter(canSee).map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                title={collapsed ? it.label : undefined}
                onClick={() => setMobileOpen(false)}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`
                }
              >
                <Icon size={16} strokeWidth={1.8} />
                {!collapsed && <span>{it.label}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className={`border-t border-[var(--border)] ${collapsed ? "p-2" : "p-4"}`}>
          {!collapsed && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[var(--surface-2)] border border-[var(--border)] flex items-center justify-center overflow-hidden flex-shrink-0">
                {user?.foto_url ? (
                  <img src={user.foto_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-bold text-[var(--gold)] text-xs">
                    {(user?.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-[var(--text)] truncate" data-testid="user-name">{user?.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{user?.perfil}</div>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title={collapsed ? "Sair" : undefined}
            className={`btn-ghost w-full text-xs flex items-center justify-center gap-2 ${collapsed ? "px-0" : ""}`}
          >
            <LogOut size={14} />
            {!collapsed && "Sair"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 min-h-screen min-w-0 transition-all duration-300 ${collapsed ? "md:ml-16" : "md:ml-64"}`}>
        <header className="h-14 border-b border-[var(--border)] bg-[var(--surface)]/80 backdrop-blur-md flex items-center justify-between px-4 md:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="p-2 -ml-2 hover:bg-[var(--surface-2)] rounded text-[var(--text)]"
              data-testid="hamburger-button"
              aria-label="Menu"
            >
              <Menu size={18} />
            </button>
            <div className="hidden sm:flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--success)] animate-pulse" />
              <span className="label-mil">Núcleo Avançado · Sementes de Futuro</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 px-2.5"
              data-testid="theme-toggle"
              title={theme === "light" ? "Mudar para Modo Dark" : "Mudar para Modo White"}
            >
              {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
              <span className="hidden sm:inline">{theme === "light" ? "Dark" : "White"}</span>
            </button>
            <div className="hidden md:block font-mono text-xs text-[var(--text-muted)]">
              {new Date().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
            </div>
          </div>
        </header>
        <div className="p-4 md:p-6 max-w-[1600px] mx-auto min-w-0 overflow-x-hidden">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
